import { Campaign, CampaignCategoryStats, ShowApiResponse, TopAdvertiser } from '@/types';
import { ExportAdvertiser, ExportAdvertisersApiResponse } from '@/types/api/advertisers';
import { ExportCampaign, ExportCampaignsApiResponse } from '@/types/api/campaigns';
import { ExportCampaignTypes, ExportCampaignTypesApiResponse } from '@/types/api/campaign-types';
import { ExportShow, ExportShowsApiResponse } from '@/types/api/shows';
import { LevelOfDetail } from '@/types';
import { DateString } from '@/types/state/filter';
import { addDays } from '../dateUtils';

// TODO: remove this file once payouts endpoints are updated to return ZIP file directly

export type CSVGenerator<T> = (data: T) => {
  headers: string[];
  rows: (string | number)[][];
};

const escapeField = (value: string | null) => {
  if (!value) return '';
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    // Escape quotes by doubling them and wrap the whole thing in quotes
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
};

const escapeRow = (row: (string | number | null)[]) =>
  row.map((value) => escapeField(String(value)));

/**
 * Introduced to maintain backwards compatibility when introducing DateString return 'yyyy/mm/dd'
 * TODO: consider some other format. E,g. yyyy-mm-dd or whatever is appropriate for the user locale.
 */
const formatYyyyMmDdWithSlashes = (dateString: DateString) => dateString.split('T')[0].replace(/-/g, '/');

const formattors = {
  advertiser: (value: string) => (value ? value : 'Unknown'),
  amount: (value: number) => value.toFixed(2),
  month: (value: number) => new Date(value, 0).toLocaleString('default', { month: 'long' }),
  weekRange: (periodStart: DateString) => {
    return `${formatYyyyMmDdWithSlashes(periodStart)} - ${formatYyyyMmDdWithSlashes(addDays(periodStart, 6))}`;
  },
};

export const generateAdvertisersCSV: CSVGenerator<TopAdvertiser[]> = (data) => ({
  headers: ['Advertiser', 'Categories', 'Earnings', 'Currency'],
  rows: data.map((advertiser) =>
    escapeRow([
      formattors.advertiser(advertiser.brand),
      advertiser.categories.length
        ? advertiser.categories.map((cat) => cat.name).join('; ')
        : 'N/A - No category',
      formattors.amount(advertiser.total?.amount),
      advertiser.total?.currency,
    ])
  ),
});

export const generateCampaignsCSV: CSVGenerator<Campaign[]> = (data) => ({
  headers: ['Show, Advertiser', 'Campaign Type', 'Impressions', 'Earnings', 'Currency'],
  rows: Object.values(data).map((campaign) =>
    escapeRow([
      campaign.show.name,
      formattors.advertiser(campaign.brand),
      campaign.type.name,
      campaign.impressions,
      formattors.amount(campaign.total.amount),
      campaign.total.currency ?? null,
    ])
  ),
});

export const generateCampaignTypesCSV: CSVGenerator<Record<string, CampaignCategoryStats>> = (
  data
) => {
  const campaignTypesToHideImpressions = ['branded content', 'social', 'other'];
  return {
    headers: ['Campaign Type', 'Impressions', 'Earnings', 'Currency'],
    rows: Object.values(data).map((campaignType) =>
      escapeRow([
        campaignType.name,
        !campaignTypesToHideImpressions.includes(campaignType.name.toLowerCase())
          ? campaignType.impressions
          : 'N/A',
        formattors.amount(campaignType.total.amount),
        campaignType.total.currency ?? null,
      ])
    ),
  };
};

export const generateShowsCSV: CSVGenerator<ShowApiResponse[]> = (data) => ({
  headers: [
    'Show',
    'Impressions',
    'Ads Earnings',
    'Sponsorships Earnings',
    'Branded Content Earnings',
    'Programmatic Earnings',
    'Social Earnings',
    'Other Earnings',
    'Total Estimated Earnings',
    'Currency',
  ],
  rows: data.map((show) =>
    escapeRow([
      show.name,
      show.impressions,
      formattors.amount(show.revenue.ads.amount),
      formattors.amount(show.revenue.sponsorships.amount),
      formattors.amount(show.revenue.branded_content.amount),
      formattors.amount(show.revenue.programmatic.amount),
      formattors.amount(show.revenue.social.amount),
      formattors.amount(show.revenue.other.amount),
      formattors.amount(show.revenue.total.amount),
      show.revenue.total?.currency ?? null,
    ])
  ),
});

/*
 * V2 generators for newer /export endpoint data
 */

export type CSVGeneratorWithBreakdown<T> = (
  data: T,
  breakdown: LevelOfDetail
) => {
  headers: string[];
  rows: (string | number)[][];
};

function getBreakdownHeaders(breakdown: LevelOfDetail) {
  switch (breakdown) {
    case LevelOfDetail.daily:
      return ['Date'];
    case LevelOfDetail.weekly:
      return ['Year', 'Week', 'Week Number'];
    case LevelOfDetail.monthly:
      return ['Year', 'Month'];
    case LevelOfDetail.total:
    default:
      return [];
  }
}

function getBreakdownData(
  data: ExportAdvertiser | ExportCampaign | ExportCampaignTypes | ExportShow,
  breakdown: LevelOfDetail
) {
  switch (breakdown) {
    case LevelOfDetail.daily:
      if (!('date' in data) || !data.date) {
        throw new Error('Daily breakdown data missing date field');
      }

      return [data.date];
    case LevelOfDetail.weekly:
      if (!('year' in data) || !data.year) {
        throw new Error('Weekly breakdown data missing year field');
      }

      if (!('week' in data) || !data.week) {
        throw new Error('Weekly breakdown data missing week field');
      }

      if (!('period_start' in data) || !data.period_start) {
        throw new Error('Weekly breakdown data missing period_start field');
      }

      // TODO: period start to range
      return [data.year, formattors.weekRange(data.period_start as DateString), data.week];
    case LevelOfDetail.monthly:
      if (!('year' in data) || !data.year) {
        throw new Error('Monthly breakdown data missing year field');
      }

      if (!('month' in data) || !data.month) {
        throw new Error('Monthly breakdown data missing month field');
      }

      // TODO: month to string
      return [data.year, formattors.month(data.month)];
    case LevelOfDetail.total:
    default:
      return [];
  }
}

export const generateAdvertisersCSVWithBreakdown: CSVGeneratorWithBreakdown<
  ExportAdvertisersApiResponse
> = (data, breakdown) => {
  const breakdownHeaders = getBreakdownHeaders(breakdown);

  return {
    headers: [...breakdownHeaders, 'Advertiser', 'Categories', 'Earnings', 'Currency'],
    rows: data.map((advertiser) =>
      escapeRow([
        ...getBreakdownData(advertiser, breakdown),
        formattors.advertiser(advertiser.brand),
        advertiser.categories?.length
          ? advertiser.categories.split(',').join('; ')
          : 'N/A - No category',
        formattors.amount(advertiser.earnings),
        advertiser?.currency,
      ])
    ),
  };
};

export const generateCampaignsCSVWithBreakdown: CSVGeneratorWithBreakdown<
  ExportCampaignsApiResponse
> = (data, breakdown) => {
  const breakdownHeaders = getBreakdownHeaders(breakdown);

  return {
    headers: [
      ...breakdownHeaders,
      'Show',
      'Advertiser',
      'Campaign Type',
      'Impressions',
      'Earnings',
      'Currency',
    ],
    rows: Object.values(data).map((campaign) =>
      escapeRow([
        ...getBreakdownData(campaign, breakdown),
        campaign.show_name,
        formattors.advertiser(campaign.brand ?? ''),
        campaign.campaign_type,
        campaign.impressions,
        formattors.amount(campaign.earnings),
        campaign.currency,
      ])
    ),
  };
};

export const generateCampaignTypesCSVWithBreakdown: CSVGeneratorWithBreakdown<
  ExportCampaignTypesApiResponse
> = (data, breakdown) => {
  const breakdownHeaders = getBreakdownHeaders(breakdown);
  const campaignTypesToHideImpressions = ['branded content', 'social', 'other'];

  return {
    headers: [...breakdownHeaders, 'Campaign Type', 'Impressions', 'Earnings', 'Currency'],
    rows: data.map((campaignTypes) =>
      escapeRow([
        ...getBreakdownData(campaignTypes, breakdown),
        campaignTypes.product_category,
        !campaignTypesToHideImpressions.includes(campaignTypes.product_category.toLowerCase())
          ? campaignTypes.impressions
          : 'N/A',
        formattors.amount(campaignTypes.amount),
        campaignTypes.podcaster_currency ?? null,
      ])
    ),
  };
};

export const generateShowsCSVWithBreakdown: CSVGeneratorWithBreakdown<ExportShowsApiResponse> = (
  data,
  breakdown
) => {
  const breakdownHeaders = getBreakdownHeaders(breakdown);

  return {
    headers: [
      ...breakdownHeaders,
      'Show',
      'Impressions',
      'Ads Earnings',
      'Sponsorships Earnings',
      'Branded Content Earnings',
      'Programmatic Earnings',
      'Social Earnings',
      'Other Earnings',
      'Total Estimated Earnings',
      'Currency',
    ],
    rows: data.map((show) =>
      escapeRow([
        ...getBreakdownData(show, breakdown),
        show.show_name,
        show.impressions,
        formattors.amount(show.ads),
        formattors.amount(show.sponsorships),
        formattors.amount(show.branded_content),
        formattors.amount(show.programmatic),
        formattors.amount(show.social),
        formattors.amount(show.other),
        formattors.amount(show.total),
        show.podcaster_currency,
      ])
    ),
  };
};
